import { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import axios from 'axios';
import { Plus, Trash2, Edit, PlayCircle, ChevronDown, ChevronUp, Loader2, BookOpen, FileText, X, Languages, Film, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { ChunkedUploader } from '../components/ChunkedUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASES = [
    { value: 'safe_start', label: 'SAFE START (Weeks 1-2)' },
    { value: 'building_consistency', label: 'BUILDING CONSISTENCY (Weeks 3-6)' },
    { value: 'capital_confidence', label: 'CAPITAL CONFIDENCE (Week 6+)' }
];

const LANGUAGES = [
    { value: 'all', label: '🌍 All Languages', flag: '🌍' },
    { value: 'en', label: '🇬🇧 English Only', flag: '🇬🇧' },
    { value: 'ar', label: '🇸🇦 Arabic Only (العربية)', flag: '🇸🇦' },
    { value: 'ur', label: '🇵🇰 Urdu Only (اردو)', flag: '🇵🇰' },
    { value: 'fr', label: '🇫🇷 French Only (Français)', flag: '🇫🇷' },
];

const LANG_FLAG = { all: '🌍', en: '🇬🇧', ar: '🇸🇦', ur: '🇵🇰', fr: '🇫🇷' };
const LANG_NAME = { all: 'All', en: 'EN', ar: 'AR', ur: 'UR', fr: 'FR' };

export default function AdminCourses() {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedCourse, setExpandedCourse] = useState(null);
    const [courseLessons, setCourseLessons] = useState({});
    
    // Course form state
    const [courseDialogOpen, setCourseDialogOpen] = useState(false);
    const [editingCourse, setEditingCourse] = useState(null);
    const [courseForm, setCourseForm] = useState({ title: '', description: '', phase: 'safe_start', order: 0, language: 'all' });
    const [savingCourse, setSavingCourse] = useState(false);
    
    // Lesson form state
    const [lessonDialogOpen, setLessonDialogOpen] = useState(false);
    const [lessonCourseId, setLessonCourseId] = useState(null);
    const [editingLesson, setEditingLesson] = useState(null);
    const [lessonForm, setLessonForm] = useState({ title: '', description: '', youtube_url: '', order: 0 });
    const [savingLesson, setSavingLesson] = useState(false);
    
    // Delete confirmation
    const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: '' });
    
    // Translation state
    const [translatingCourseId, setTranslatingCourseId] = useState(null);

    useEffect(() => {
        fetchCourses();
    }, []);

    const fetchCourses = async () => {
        try {
            const { data } = await axios.get(`${API}/courses`, { withCredentials: true });
            setCourses(data);
        } catch (e) {
            console.error('Error fetching courses:', e);
            toast.error('Failed to load courses');
        } finally {
            setLoading(false);
        }
    };

    const fetchCourseLessons = async (courseId) => {
        try {
            const { data } = await axios.get(`${API}/courses/${courseId}`, { withCredentials: true });
            setCourseLessons(prev => ({ ...prev, [courseId]: data.lessons || [] }));
        } catch (e) {
            console.error('Error fetching lessons:', e);
        }
    };

    const toggleCourseExpand = async (courseId) => {
        if (expandedCourse === courseId) {
            setExpandedCourse(null);
        } else {
            setExpandedCourse(courseId);
            if (!courseLessons[courseId]) {
                await fetchCourseLessons(courseId);
            }
        }
    };

    // Course CRUD
    const openCourseDialog = (course = null) => {
        if (course) {
            setEditingCourse(course);
            setCourseForm({
                title: course.title,
                description: course.description,
                phase: course.phase,
                order: course.order || 0,
                language: course.language || 'all'
            });
        } else {
            setEditingCourse(null);
            setCourseForm({ title: '', description: '', phase: 'safe_start', order: 0, language: 'all' });
        }
        setCourseDialogOpen(true);
    };

    const saveCourse = async () => {
        if (!courseForm.title || !courseForm.description) {
            toast.error('Please fill in all required fields');
            return;
        }
        
        setSavingCourse(true);
        try {
            if (editingCourse) {
                await axios.put(`${API}/admin/courses/${editingCourse.id}`, courseForm, { withCredentials: true });
                toast.success('Course updated successfully');
            } else {
                await axios.post(`${API}/admin/courses`, courseForm, { withCredentials: true });
                toast.success('Course created successfully');
            }
            setCourseDialogOpen(false);
            fetchCourses();
        } catch (e) {
            console.error('Error saving course:', e);
            toast.error(e.response?.data?.detail || 'Failed to save course');
        } finally {
            setSavingCourse(false);
        }
    };

    const deleteCourse = async () => {
        try {
            await axios.delete(`${API}/admin/courses/${deleteDialog.id}`, { withCredentials: true });
            toast.success('Course deleted successfully');
            setDeleteDialog({ open: false, type: null, id: null, name: '' });
            fetchCourses();
        } catch (e) {
            console.error('Error deleting course:', e);
            toast.error('Failed to delete course');
        }
    };

    const translateCourse = async (courseId, courseTitle) => {
        if (translatingCourseId) return;
        setTranslatingCourseId(courseId);
        const toastId = toast.loading(`Translating "${courseTitle}" into Arabic, Urdu, and French...`);
        try {
            await axios.post(`${API}/admin/courses/${courseId}/translate`, {}, { withCredentials: true });
            toast.success('Translated into Arabic, Urdu & French!', { id: toastId });
            fetchCourses();
        } catch (e) {
            console.error('Translate error:', e);
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Translation failed. Please try again.', { id: toastId });
        } finally {
            setTranslatingCourseId(null);
        }
    };

    // Lesson CRUD
    const openLessonDialog = (courseId, lesson = null) => {
        setLessonCourseId(courseId);
        if (lesson) {
            setEditingLesson(lesson);
            setLessonForm({
                title: lesson.title,
                description: lesson.description || '',
                youtube_url: lesson.youtube_url || '',
                video_file_url: lesson.video_file_url || null,
                order: lesson.order || 0,
                materials: lesson.materials || []
            });
        } else {
            setEditingLesson(null);
            setLessonForm({ title: '', description: '', youtube_url: '', video_file_url: null, order: 0, materials: [] });
        }
        setLessonDialogOpen(true);
    };

    const addMaterialRow = () => {
        setLessonForm(prev => ({ ...prev, materials: [...(prev.materials || []), { title: '', url: '' }] }));
    };

    const updateMaterial = (idx, field, value) => {
        setLessonForm(prev => ({
            ...prev,
            materials: prev.materials.map((m, i) => i === idx ? { ...m, [field]: value } : m)
        }));
    };

    const removeMaterial = (idx) => {
        setLessonForm(prev => ({ ...prev, materials: prev.materials.filter((_, i) => i !== idx) }));
    };

    const saveLesson = async () => {
        if (!lessonForm.title) {
            toast.error('Title is required');
            return;
        }
        if (!lessonForm.youtube_url && !lessonForm.video_file_url) {
            toast.error('Provide a YouTube URL or upload a video file');
            return;
        }
        
        // Validate materials (both title and url required for non-empty rows)
        const validMaterials = (lessonForm.materials || []).filter(m => m.title && m.url);
        const invalidMaterials = (lessonForm.materials || []).filter(m => (m.title || m.url) && (!m.title || !m.url));
        if (invalidMaterials.length > 0) {
            toast.error('Each material needs both a title and URL');
            return;
        }
        
        const payload = { ...lessonForm, materials: validMaterials };
        
        setSavingLesson(true);
        try {
            if (editingLesson) {
                await axios.put(`${API}/admin/lessons/${editingLesson.id}`, payload, { withCredentials: true });
                toast.success('Lesson updated successfully');
            } else {
                await axios.post(`${API}/admin/courses/${lessonCourseId}/lessons`, payload, { withCredentials: true });
                toast.success('Lesson added successfully');
            }
            setLessonDialogOpen(false);
            fetchCourseLessons(lessonCourseId);
            fetchCourses();
        } catch (e) {
            console.error('Error saving lesson:', e);
            toast.error(e.response?.data?.detail || 'Failed to save lesson');
        } finally {
            setSavingLesson(false);
        }
    };

    const deleteLesson = async () => {
        try {
            await axios.delete(`${API}/admin/lessons/${deleteDialog.id}`, { withCredentials: true });
            toast.success('Lesson deleted successfully');
            setDeleteDialog({ open: false, type: null, id: null, name: '' });
            if (expandedCourse) {
                fetchCourseLessons(expandedCourse);
            }
            fetchCourses();
        } catch (e) {
            console.error('Error deleting lesson:', e);
            toast.error('Failed to delete lesson');
        }
    };

    const getPhaseColor = (phase) => {
        const colors = {
            safe_start: '#3B82F6',
            building_consistency: '#8B5CF6',
            capital_confidence: '#D4AF37'
        };
        return colors[phase] || '#94A3B8';
    };

    const formatPhaseName = (phase) => {
        const names = {
            safe_start: 'SAFE START',
            building_consistency: 'BUILDING CONSISTENCY',
            capital_confidence: 'CAPITAL CONFIDENCE'
        };
        return names[phase] || phase;
    };

    if (loading) {
        return (
            <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
                <Header />
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="w-10 h-10 animate-spin" style={{ color: 'var(--primary-color)' }} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }} data-testid="courses-management-title">
                            Manage Courses
                        </h1>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            Create and manage your course curriculum
                        </p>
                    </div>
                    <Button onClick={() => openCourseDialog()} className="btn-primary" data-testid="add-course-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Course
                    </Button>
                </div>

                {/* Courses List */}
                {courses.length === 0 ? (
                    <div className="card-surface p-12 text-center" data-testid="no-courses-message">
                        <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                        <p className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>No courses yet</p>
                        <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>Create your first course to get started</p>
                        <Button onClick={() => openCourseDialog()} className="btn-secondary">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Course
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="courses-list">
                        {courses.map((course) => (
                            <div key={course.id} className="card-surface overflow-hidden" data-testid={`course-item-${course.id}`}>
                                {/* Course Header */}
                                <div className="p-5 flex items-center justify-between">
                                    <div className="flex items-center gap-4 flex-1 min-w-0">
                                        <button
                                            onClick={() => toggleCourseExpand(course.id)}
                                            className="p-2 rounded-lg transition-colors"
                                            style={{ backgroundColor: 'var(--background-elevated)' }}
                                            data-testid={`expand-course-${course.id}`}
                                        >
                                            {expandedCourse === course.id ? (
                                                <ChevronUp className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                                            ) : (
                                                <ChevronDown className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                                            )}
                                        </button>
                                        
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 mb-1 flex-wrap">
                                                <span 
                                                    className="px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0"
                                                    style={{ 
                                                        backgroundColor: `${getPhaseColor(course.phase)}15`,
                                                        color: getPhaseColor(course.phase),
                                                        border: `1px solid ${getPhaseColor(course.phase)}30`
                                                    }}
                                                >
                                                    {formatPhaseName(course.phase)}
                                                </span>
                                                <span
                                                    className="px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1 flex-shrink-0"
                                                    style={{
                                                        backgroundColor: 'var(--background-elevated)',
                                                        color: 'var(--text-secondary)',
                                                        border: '1px solid var(--border-subtle)'
                                                    }}
                                                    data-testid={`course-language-badge-${course.id}`}
                                                >
                                                    <span>{LANG_FLAG[course.language || 'all']}</span>
                                                    <span>{LANG_NAME[course.language || 'all']}</span>
                                                </span>
                                                <h3 className="font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                                                    {course.title}
                                                </h3>
                                            </div>
                                            <p className="text-sm truncate" style={{ color: 'var(--text-muted)' }}>
                                                {course.lesson_count || 0} lessons
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-2 ml-4">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => translateCourse(course.id, course.title)}
                                            disabled={translatingCourseId === course.id}
                                            className="h-8 px-3"
                                            style={{ color: course.has_translations ? 'var(--accent-success)' : 'var(--primary-color)' }}
                                            title={course.has_translations ? 'Re-translate (overwrites existing)' : 'Translate to Arabic, Urdu & French using AI'}
                                            data-testid={`translate-course-${course.id}`}
                                        >
                                            {translatingCourseId === course.id ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <>
                                                    <Languages className="w-4 h-4 mr-1" />
                                                    <span className="hidden sm:inline text-xs">
                                                        {course.has_translations ? 'Re-translate' : 'Translate'}
                                                    </span>
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => openCourseDialog(course)}
                                            data-testid={`edit-course-${course.id}`}
                                        >
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setDeleteDialog({ open: true, type: 'course', id: course.id, name: course.title })}
                                            data-testid={`delete-course-${course.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" style={{ color: 'var(--accent-danger)' }} />
                                        </Button>
                                    </div>
                                </div>

                                {/* Lessons Section */}
                                {expandedCourse === course.id && (
                                    <div className="border-t px-5 py-4" style={{ borderColor: 'var(--border-subtle)', backgroundColor: 'var(--background-elevated)' }}>
                                        <div className="flex items-center justify-between mb-4">
                                            <h4 className="font-medium text-sm" style={{ color: 'var(--text-secondary)' }}>Lessons</h4>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openLessonDialog(course.id)}
                                                className="text-xs"
                                                data-testid={`add-lesson-${course.id}`}
                                            >
                                                <Plus className="w-3 h-3 mr-1" />
                                                Add Lesson
                                            </Button>
                                        </div>
                                        
                                        {!courseLessons[course.id] ? (
                                            <div className="flex items-center justify-center py-4">
                                                <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} />
                                            </div>
                                        ) : courseLessons[course.id].length === 0 ? (
                                            <p className="text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>No lessons yet</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {courseLessons[course.id].map((lesson, index) => (
                                                    <div key={lesson.id} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--background-surface)' }}>
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--background-base)' }}>
                                                                <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{index + 1}</span>
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="font-medium text-sm truncate" style={{ color: 'var(--text-primary)' }}>{lesson.title}</p>
                                                                {lesson.youtube_id && (
                                                                    <p className="text-xs flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                                                                        <PlayCircle className="w-3 h-3" />
                                                                        YouTube Video
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1 ml-2">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => openLessonDialog(course.id, lesson)}
                                                                data-testid={`edit-lesson-${lesson.id}`}
                                                            >
                                                                <Edit className="w-3 h-3" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => setDeleteDialog({ open: true, type: 'lesson', id: lesson.id, name: lesson.title })}
                                                                data-testid={`delete-lesson-${lesson.id}`}
                                                            >
                                                                <Trash2 className="w-3 h-3" style={{ color: 'var(--accent-danger)' }} />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </main>

            {/* Course Dialog */}
            <Dialog open={courseDialogOpen} onOpenChange={setCourseDialogOpen}>
                <DialogContent style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border-default)' }}>
                    <DialogHeader>
                        <DialogTitle style={{ color: 'var(--text-primary)' }}>
                            {editingCourse ? 'Edit Course' : 'Add New Course'}
                        </DialogTitle>
                        <DialogDescription style={{ color: 'var(--text-muted)' }}>
                            {editingCourse ? 'Update course details below.' : 'Create a new course and assign it to a phase.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                            <Label htmlFor="course-title" style={{ color: 'var(--text-secondary)' }}>Title *</Label>
                            <Input
                                id="course-title"
                                value={courseForm.title}
                                onChange={(e) => setCourseForm(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="e.g., Introduction to Forex Trading"
                                className="input-field"
                                data-testid="course-title-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="course-description" style={{ color: 'var(--text-secondary)' }}>Description *</Label>
                            <Textarea
                                id="course-description"
                                value={courseForm.description}
                                onChange={(e) => setCourseForm(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Describe what students will learn..."
                                rows={3}
                                className="input-field"
                                data-testid="course-description-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="course-phase" style={{ color: 'var(--text-secondary)' }}>Phase *</Label>
                            <Select
                                value={courseForm.phase}
                                onValueChange={(value) => setCourseForm(prev => ({ ...prev, phase: value }))}
                            >
                                <SelectTrigger className="input-field" data-testid="course-phase-select">
                                    <SelectValue placeholder="Select phase" />
                                </SelectTrigger>
                                <SelectContent style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border-default)' }}>
                                    {PHASES.map(phase => (
                                        <SelectItem key={phase.value} value={phase.value}>
                                            {phase.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="course-language" style={{ color: 'var(--text-secondary)' }}>Target Language *</Label>
                            <Select
                                value={courseForm.language}
                                onValueChange={(value) => setCourseForm(prev => ({ ...prev, language: value }))}
                            >
                                <SelectTrigger className="input-field" data-testid="course-language-select">
                                    <SelectValue placeholder="Select language" />
                                </SelectTrigger>
                                <SelectContent style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border-default)' }}>
                                    {LANGUAGES.map(lang => (
                                        <SelectItem key={lang.value} value={lang.value}>
                                            {lang.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                "All Languages" courses are visible to every student. Language-specific courses appear only when students switch to that language.
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="course-order" style={{ color: 'var(--text-secondary)' }}>Order</Label>
                            <Input
                                id="course-order"
                                type="number"
                                value={courseForm.order}
                                onChange={(e) => setCourseForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                                className="input-field"
                                data-testid="course-order-input"
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button variant="ghost" onClick={() => setCourseDialogOpen(false)} className="btn-ghost">
                                Cancel
                            </Button>
                            <Button onClick={saveCourse} disabled={savingCourse} className="btn-primary" data-testid="save-course-btn">
                                {savingCourse ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingCourse ? 'Update' : 'Create')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Lesson Dialog */}
            <Dialog open={lessonDialogOpen} onOpenChange={setLessonDialogOpen}>
                <DialogContent className="max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border-default)' }}>
                    <DialogHeader>
                        <DialogTitle style={{ color: 'var(--text-primary)' }}>
                            {editingLesson ? 'Edit Lesson' : 'Add New Lesson'}
                        </DialogTitle>
                        <DialogDescription style={{ color: 'var(--text-muted)' }}>
                            {editingLesson ? 'Update lesson details and materials.' : 'Add a video lesson with optional downloadable materials.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                            <Label htmlFor="lesson-title" style={{ color: 'var(--text-secondary)' }}>Title *</Label>
                            <Input
                                id="lesson-title"
                                value={lessonForm.title}
                                onChange={(e) => setLessonForm(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="e.g., Understanding Currency Pairs"
                                className="input-field"
                                data-testid="lesson-title-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="lesson-description" style={{ color: 'var(--text-secondary)' }}>Description</Label>
                            <Textarea
                                id="lesson-description"
                                value={lessonForm.description}
                                onChange={(e) => setLessonForm(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Brief description of the lesson..."
                                rows={2}
                                className="input-field"
                                data-testid="lesson-description-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="lesson-youtube" style={{ color: 'var(--text-secondary)' }}>Video Source *</Label>
                            <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
                                Either paste a YouTube URL OR upload a video file (up to 2 GB)
                            </p>
                            <Input
                                id="lesson-youtube"
                                value={lessonForm.youtube_url}
                                onChange={(e) => setLessonForm(prev => ({ ...prev, youtube_url: e.target.value }))}
                                placeholder="https://www.youtube.com/watch?v=..."
                                className="input-field"
                                data-testid="lesson-youtube-input"
                            />
                            <p className="text-xs text-center my-1" style={{ color: 'var(--text-muted)' }}>— OR —</p>
                            {lessonForm.video_file_url ? (
                                <div className="p-3 rounded flex items-center justify-between" style={{ backgroundColor: 'var(--primary-muted)', border: '1px solid var(--border-highlight)' }} data-testid="lesson-video-uploaded">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <Film className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--primary-color)' }} />
                                        <span className="text-sm truncate font-mono" style={{ color: 'var(--text-primary)' }}>{lessonForm.video_file_url.split('/').pop()}</span>
                                    </div>
                                    <button type="button" onClick={() => setLessonForm(prev => ({ ...prev, video_file_url: null }))} className="text-xs underline flex-shrink-0" style={{ color: 'var(--accent-danger)' }} data-testid="remove-uploaded-video">
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                <ChunkedUploader
                                    kind="video"
                                    maxSizeMB={2048}
                                    label="Upload Video File (up to 2 GB)"
                                    onSuccess={(data) => setLessonForm(prev => ({ ...prev, video_file_url: data.path }))}
                                />
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="lesson-order" style={{ color: 'var(--text-secondary)' }}>Order</Label>
                            <Input
                                id="lesson-order"
                                type="number"
                                value={lessonForm.order}
                                onChange={(e) => setLessonForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                                className="input-field"
                                data-testid="lesson-order-input"
                            />
                        </div>
                        
                        {/* Materials Section */}
                        <div className="space-y-3 pt-2 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                            <div className="flex items-center justify-between">
                                <Label style={{ color: 'var(--text-secondary)' }} className="flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    PDF Materials & Resources
                                </Label>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={addMaterialRow}
                                    className="text-xs h-7 px-2"
                                    style={{ color: 'var(--primary-color)' }}
                                    data-testid="add-material-btn"
                                >
                                    <Plus className="w-3 h-3 mr-1" />
                                    Add Material
                                </Button>
                            </div>
                            
                            {(lessonForm.materials || []).length === 0 ? (
                                <p className="text-xs italic" style={{ color: 'var(--text-muted)' }}>
                                    No materials yet. Click &quot;Add Material&quot; to attach PDFs (upload or paste URL).
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {lessonForm.materials.map((material, idx) => (
                                        <div key={idx} className="p-3 rounded space-y-2" style={{ backgroundColor: 'var(--background-elevated)', border: '1px solid var(--border-subtle)' }} data-testid={`material-row-${idx}`}>
                                            <div className="flex gap-2 items-start">
                                                <Input
                                                    value={material.title}
                                                    onChange={(e) => updateMaterial(idx, 'title', e.target.value)}
                                                    placeholder="Title (e.g., Risk Management PDF)"
                                                    className="input-field flex-1"
                                                    data-testid={`material-title-${idx}`}
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeMaterial(idx)}
                                                    className="h-10 px-2 flex-shrink-0"
                                                    data-testid={`remove-material-${idx}`}
                                                >
                                                    <X className="w-4 h-4" style={{ color: 'var(--accent-danger)' }} />
                                                </Button>
                                            </div>
                                            {material.url && !material.url.startsWith('http') ? (
                                                /* Uploaded PDF — show as chip */
                                                <div className="flex items-center justify-between gap-2 px-3 py-2 rounded" style={{ backgroundColor: 'var(--primary-muted)', border: '1px solid var(--border-highlight)' }}>
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <FileText className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--primary-color)' }} />
                                                        <span className="text-xs truncate font-mono" style={{ color: 'var(--text-primary)' }}>{material.url.split('/').pop()}</span>
                                                    </div>
                                                    <button type="button" onClick={() => updateMaterial(idx, 'url', '')} className="text-xs underline flex-shrink-0" style={{ color: 'var(--accent-danger)' }}>
                                                        Replace
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <Input
                                                        value={material.url}
                                                        onChange={(e) => updateMaterial(idx, 'url', e.target.value)}
                                                        placeholder="Paste URL (https://...) or upload below"
                                                        className="input-field"
                                                        data-testid={`material-url-${idx}`}
                                                    />
                                                    <ChunkedUploader
                                                        kind="pdf"
                                                        maxSizeMB={50}
                                                        label="Upload PDF Instead"
                                                        onSuccess={(data) => updateMaterial(idx, 'url', data.path)}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        <div className="flex justify-end gap-3 pt-4">
                            <Button variant="ghost" onClick={() => setLessonDialogOpen(false)} className="btn-ghost">
                                Cancel
                            </Button>
                            <Button onClick={saveLesson} disabled={savingLesson} className="btn-primary" data-testid="save-lesson-btn">
                                {savingLesson ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingLesson ? 'Update' : 'Add')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, id: null, name: '' })}>
                <AlertDialogContent style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border-default)' }}>
                    <AlertDialogHeader>
                        <AlertDialogTitle style={{ color: 'var(--text-primary)' }}>Delete {deleteDialog.type}?</AlertDialogTitle>
                        <AlertDialogDescription style={{ color: 'var(--text-secondary)' }}>
                            Are you sure you want to delete "{deleteDialog.name}"? This action cannot be undone.
                            {deleteDialog.type === 'course' && ' All lessons in this course will also be deleted.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="btn-ghost">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={deleteDialog.type === 'course' ? deleteCourse : deleteLesson}
                            style={{ backgroundColor: 'var(--accent-danger)' }}
                            data-testid="confirm-delete-btn"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
